import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Users } from "lucide-react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import BulkCustomerUpload from "../components/BulkCustomerUpload";

const BulkCustomerImport = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const navigate = useNavigate();

  const handleUploadComplete = () => {
    // Refresh the page or navigate back to customers list
    // You can also trigger a data refresh here
    console.log("Bulk upload completed successfully");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Bulk Import Customers"
          subtitle="Import multiple customers from a CSV or Excel file"
        />

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center space-y-6">
            {/* Icon */}
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Upload className="w-8 h-8 text-blue-600" />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-gray-900">
                Import Customers in Bulk
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Upload a CSV or Excel file containing customer information to
                quickly add multiple customers to your account. Duplicate
                customers will be automatically skipped.
              </p>
            </div>

            {/* Features List */}
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Bulk Import</h3>
                <p className="text-sm text-gray-600">
                  Import hundreds of customers at once from your spreadsheet
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">
                  Smart Validation
                </h3>
                <p className="text-sm text-gray-600">
                  Automatic validation and duplicate detection
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">
                  Multiple Formats
                </h3>
                <p className="text-sm text-gray-600">
                  Support for CSV and Excel files (.csv, .xlsx, .xls)
                </p>
              </div>
            </div>

            {/* Action Button */}
            <div className="pt-6">
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
              >
                Start Bulk Import
              </button>
            </div>

            {/* Additional Info */}
            <div className="bg-gray-50 rounded-lg p-6 max-w-2xl mx-auto">
              <h4 className="font-medium text-gray-900 mb-3">
                File Requirements:
              </h4>
              <ul className="text-sm text-gray-600 space-y-1 text-left">
                <li>
                  • <strong>Required columns:</strong> name, phoneNumber
                </li>
                <li>
                  • <strong>Optional columns:</strong> location, email
                </li>
                <li>
                  • <strong>File size:</strong> Maximum 5MB
                </li>
                <li>
                  • <strong>Supported formats:</strong> CSV, Excel (.xlsx, .xls)
                </li>
                <li>
                  • <strong>Duplicate handling:</strong> Customers with existing
                  phone numbers or emails will be skipped
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <BulkCustomerUpload
          onUploadComplete={handleUploadComplete}
          onClose={() => setShowUploadModal(false)}
        />
      )}
    </div>
  );
};

export default BulkCustomerImport;
